/**
 * Build embed iframe HTML for YouTube and Vimeo URLs so listing videos play inline.
 * When the MLS provides only a video URL (no ObjectHtml), we can still embed it.
 *
 * Every URL that enters here goes through cleanListingMediaUrl first
 * (2026-10-14): an MLS video link rewritten by a mail scanner (Microsoft
 * SafeLinks, Proofpoint, Google redirect) is unwrapped to the real target, and
 * no URL that carries an email address leaves this module.
 */
import { cleanListingMediaUrl } from '@/lib/listing/listing-media-url'

/** Watch, short, embed, youtu.be — video IDs are 11 chars. */
const YOUTUBE_REGEX =
  /(?:youtube\.com\/watch\?[^#]*v=|youtube\.com\/(?:embed\/|shorts\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

/**
 * A Vimeo video id, and the privacy hash when the path carries one. An
 * unlisted Vimeo video is shared as vimeo.com/<id>/<hash> and embedded as
 * player.vimeo.com/video/<id>?h=<hash>; asked for without the hash, the player
 * shows "Sorry, this video does not exist" instead of the video.
 */
const VIMEO_REGEX = /vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-f]{6,})(?![0-9a-z]))?/i
/** The `h` query form. A src pulled out of MLS iframe markup can still read `&amp;h=`. */
const VIMEO_HASH_PARAM = /[?&](?:amp;)?h=([0-9a-f]{6,})(?![0-9a-z])/i

export type VimeoRef = {
  id: string
  /** The unlisted-video privacy hash, or null for a public video. */
  hash: string | null
}

/** The Vimeo video a URL points at, with its privacy hash from either shape. Pure. */
export function parseVimeoRef(url: string): VimeoRef | null {
  if (!url || typeof url !== 'string') return null
  const m = VIMEO_REGEX.exec(url)
  if (!m) return null
  return { id: m[1], hash: m[2] ?? VIMEO_HASH_PARAM.exec(url)?.[1] ?? null }
}

/**
 * The player src for a Vimeo video. The hash goes first as `h`, where Vimeo's
 * own embed code puts it, then `params` in order. Every Vimeo src this repo
 * builds comes through here, so none can drop the hash again. Pure.
 */
export function vimeoPlayerSrc(ref: VimeoRef, params: Readonly<Record<string, string>> = {}): string {
  const u = new URL(`https://player.vimeo.com/video/${ref.id}`)
  if (ref.hash) u.searchParams.set('h', ref.hash)
  for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v)
  return u.toString()
}

/**
 * Two srcs for the same media (one path, different query): the one that
 * carries Vimeo's privacy hash is the one that plays, so it wins over a
 * hashless `kept`. Otherwise `kept` stands. Pure.
 */
export function preferPlayableCopy(kept: string, next: string): string {
  return !parseVimeoRef(kept)?.hash && parseVimeoRef(next)?.hash ? next : kept
}

export type ListingTileVideoEmbed = {
  kind: 'youtube' | 'vimeo' | 'matterport'
  /** Full iframe src (autoplay where policy allows). */
  src: string
  /** Optional poster when listing has no PhotoURL (YouTube only). */
  posterUrl?: string | null
}

/** True when `<video src>` can load a progressive file (not YouTube page URLs). */
export function isDirectListingVideoFileUrl(uri: string): boolean {
  const u = uri.trim().toLowerCase()
  if (!u || u.includes('<') || u.includes('>')) return false
  if (!u.startsWith('http://') && !u.startsWith('https://')) return false
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(uri)
}

function extractIframeSrcFromMarkup(raw: string): string | null {
  const m = raw.match(/<iframe[^>]+src=["']([^"']+)["']/i)
  const s = m?.[1]?.trim()
  return s && (s.startsWith('http://') || s.startsWith('https://')) ? s : null
}

/**
 * Like extractIframeSrcFromMarkup but also accepts protocol-relative srcs
 * (`//www.youtube.com/embed/...`), which MLS RETS feeds emit constantly. Returns
 * the raw src (still protocol-relative if that is how it came) for the caller to
 * normalize, or null when no usable src is present.
 */
function extractIframeSrcFromMarkupLoose(raw: string): string | null {
  const m = raw.match(/<iframe[^>]+src=["']([^"']+)["']/i)
  const s = m?.[1]?.trim()
  if (!s) return null
  return s.startsWith('http://') || s.startsWith('https://') || s.startsWith('//') ? s : null
}

/**
 * Resolve MLS video URL or ObjectHtml snippet to an embeddable iframe src for listing tiles.
 */
export function parseListingVideoEmbedForTile(urlOrHtml: string): ListingTileVideoEmbed | null {
  if (!urlOrHtml || typeof urlOrHtml !== 'string') return null
  let trimmed = urlOrHtml.trim()
  if (trimmed.includes('<iframe')) {
    const extracted = extractIframeSrcFromMarkup(trimmed)
    if (extracted) trimmed = extracted
    else return null
  }
  const cleaned = cleanListingMediaUrl(trimmed)
  if (!cleaned) return null
  trimmed = cleaned
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) return null

  const yt = trimmed.match(YOUTUBE_REGEX)
  if (yt) {
    const id = yt[1]
    return {
      kind: 'youtube',
      src: `https://www.youtube.com/embed/${id}?rel=0&autoplay=1&mute=1&playsinline=1`,
      posterUrl: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    }
  }
  const vimeo = parseVimeoRef(trimmed)
  if (vimeo) {
    return {
      kind: 'vimeo',
      src: vimeoPlayerSrc(vimeo, { autoplay: '1', muted: '1' }),
    }
  }
  const low = trimmed.toLowerCase()
  if (low.includes('matterport.com')) {
    return { kind: 'matterport', src: trimmed }
  }
  return null
}

export function getVideoEmbedHtml(url: string, autoplay = true): string | null {
  if (!url || typeof url !== 'string') return null
  const trimmed = cleanListingMediaUrl(url)
  if (!trimmed) return null
  const yt = trimmed.match(YOUTUBE_REGEX)
  if (yt) {
    const id = yt[1]
    const src = `https://www.youtube.com/embed/${id}?rel=0${autoplay ? '&autoplay=1&mute=1' : ''}`
    return `<iframe src="${escapeAttr(src)}" title="YouTube video" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen class="absolute inset-0 h-full w-full"></iframe>`
  }
  const vimeo = parseVimeoRef(trimmed)
  if (vimeo) {
    const src = vimeoPlayerSrc(vimeo, autoplay ? { autoplay: '1' } : {})
    return `<iframe src="${escapeAttr(src)}" title="Vimeo video" allow="fullscreen; picture-in-picture; autoplay" allowfullscreen class="absolute inset-0 h-full w-full"></iframe>`
  }
  return null
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Normalize a raw video / virtual-tour URL into an embeddable form + render mode:
 *   - 'iframe'    YouTube/Vimeo/Matterport (canonical embed src) + Aryeo/Cloudflare
 *   - 'video-tag' direct progressive file (.mp4/.webm/.mov)
 *   - 'link'      hosts that block framing (Dropbox folders) → watch link
 * Returns null to drop the entry (MapRight parcel maps are not walkthrough videos).
 *
 * Lives in this pure module (no server-only deps) so BOTH the server DAL
 * (getListingVideos) and CLIENT components (VideoListingCard inline play) can
 * resolve a scalar URL to the same CSP-safe embed without pulling `next/cache`
 * into the browser bundle. The embed hosts here mirror the CSP `frame-src`
 * allow-list (ci:embed-csp-parity). getListingVideos re-exports this symbol so
 * every existing `normalizeEmbed` import keeps working unchanged.
 */
export function normalizeEmbed(
  raw: string | null,
  hintSource?: string | null,
): { url: string; embedType: 'iframe' | 'video-tag' | 'link'; posterUrl?: string } | null {
  if (!raw) return null
  let url = raw.trim()
  // MLS video fields frequently arrive as an <iframe ...> snippet (sometimes
  // wrapped in a <div>, sometimes with a protocol-relative //host src). Pull the
  // src out first so the host checks below see a real URL instead of bailing at
  // the http(s) guard. (This is the #1 reason hosted embeds were silently
  // dropping out of the video feed.)
  if (url.includes('<iframe')) {
    const inner = extractIframeSrcFromMarkupLoose(url)
    if (inner) url = inner
    else return null
  }
  if (url.startsWith('//')) url = `https:${url}`
  // Unwrap a mail-scanner rewrite and refuse an email-bearing URL before any
  // host check, so a SafeLinks-wrapped YouTube embeds as YouTube and a
  // wrapper the reader cannot decode never ships as a watch link.
  const cleaned = cleanListingMediaUrl(url)
  if (!cleaned) return null
  url = cleaned
  if (!/^https?:\/\//i.test(url)) return null
  const low = url.toLowerCase()
  const hint = (hintSource ?? '').toLowerCase()

  if (low.includes('mapright') || hint.includes('mapright')) return null

  // Dropbox FIRST (before the generic .mp4 check): it blocks IFRAME framing, and
  // www.dropbox.com/<file>.mp4 serves an HTML PREVIEW, not the file — so the raw
  // share URL is useless in a <video> tag. If the link points at a video FILE,
  // rewrite to dl.dropboxusercontent.com (+ raw=1) which serves it inline, so it
  // becomes a proper SHOWCASE HERO instead of a buried watch-link. Folders stay a link.
  if (low.includes('dropbox.com')) {
    if (/\.(mp4|m4v|mov|webm)(\?|$)/i.test(low)) {
      let direct = url.replace(/:\/\/(?:www\.)?dropbox\.com/i, '://dl.dropboxusercontent.com')
      direct = direct.replace(/([?&])dl=0\b/i, '$1raw=1')
      if (!/[?&](?:dl|raw)=/i.test(direct)) direct += (direct.includes('?') ? '&' : '?') + 'raw=1'
      return { url: direct, embedType: 'video-tag' }
    }
    return { url, embedType: 'link' }
  }

  // Google Drive: agents often "host" a tour as a Drive video FILE. The /view
  // share URL blocks framing and is not a playable file, but /preview embeds a
  // player iframe. Rewrite file/d/<id>/... -> file/d/<id>/preview.
  const gdrive = url.match(/drive\.google\.com\/file\/d\/([A-Za-z0-9_-]+)/)
  if (gdrive) return { url: `https://drive.google.com/file/d/${gdrive[1]}/preview`, embedType: 'iframe' }

  const parsed = parseListingVideoEmbedForTile(url)
  if (parsed) return { url: parsed.src, embedType: 'iframe', posterUrl: parsed.posterUrl ?? undefined }

  if (isDirectListingVideoFileUrl(url)) return { url, embedType: 'video-tag' }

  // Hosted players + 3D tours that allow framing (Aryeo verified framable;
  // Matterport + Zillow 3D Home view-imx are built for MLS/IDX embedding, so
  // they stay ON-site in an iframe rather than sending the visitor off to a
  // competitor — important since these arrive via details.VirtualTours).
  if (
    low.includes('aryeo.com') ||
    low.includes('cloudflarestream.com') ||
    low.includes('videodelivery.net') ||
    low.includes('player.vimeo.com') ||
    low.includes('matterport.com') ||
    low.includes('zillow.com/view-imx') ||
    low.includes('zillow.com/view-3d-home')
  ) {
    return { url, embedType: 'iframe' }
  }

  // Anything unrecognized: frame-blocked → watch link, never a broken iframe.
  return { url, embedType: 'link' }
}

/**
 * Rewrite an embed URL into a silent BACKGROUND-video URL: autoplay, muted, loop,
 * and NO player chrome / play button — for tiles where the video plays itself.
 * Vimeo `background=1` is true no-UI mode (the cleanest); YouTube `controls=0` +
 * loop(playlist) is the closest equivalent. Non-vimeo/youtube embeds (Matterport,
 * etc.) are returned unchanged. Pure + client-safe.
 */
export function toBackgroundEmbed(url: string): string {
  try {
    const u = new URL(url)
    const host = u.hostname.toLowerCase()
    if (host.includes('vimeo.com')) {
      // A vimeo.com/<id>/<hash> page URL is rebuilt as the player URL, whose
      // hash rides as ?h=; a player URL keeps its own params (the hash with them).
      const ref = parseVimeoRef(url)
      const out = ref && host !== 'player.vimeo.com' ? new URL(vimeoPlayerSrc(ref)) : u
      if (ref?.hash && !out.searchParams.get('h')) out.searchParams.set('h', ref.hash)
      out.searchParams.set('background', '1')
      out.searchParams.set('autoplay', '1')
      out.searchParams.set('muted', '1')
      out.searchParams.set('loop', '1')
      return out.toString()
    }
    if (host.includes('youtube.com') || host.includes('youtube-nocookie.com')) {
      const id = u.pathname.split('/embed/')[1]?.split('/')[0]
      u.searchParams.set('autoplay', '1')
      u.searchParams.set('mute', '1')
      u.searchParams.set('controls', '0')
      u.searchParams.set('loop', '1')
      if (id) u.searchParams.set('playlist', id) // YouTube loop requires playlist=<id>
      u.searchParams.set('modestbranding', '1')
      u.searchParams.set('playsinline', '1')
      u.searchParams.set('rel', '0')
      return u.toString()
    }
    return url
  } catch {
    return url
  }
}

/**
 * Given a normalized embed, return a SILENT background-video version (autoplay,
 * muted, loop, NO controls / NO play button) for a self-playing listing/community
 * tile — or `null` when the host CANNOT be rendered chrome-less. Only direct video
 * files, Vimeo (`background=1`), YouTube (`controls=0`), and Cloudflare Stream
 * (`controls=false`) can be guaranteed play-button-free. Aryeo, Google Drive
 * `/preview`, Matterport, Zillow 3D and unknown hosts keep a native play button, so
 * we return null and the tile stays photo-only (Matt directive: no play button on
 * tiles). Pure + client-safe.
 */
export function toTileBackgroundVideo(embed: {
  url: string
  embedType: 'iframe' | 'video-tag' | 'link'
}): { url: string; embedType: 'iframe' | 'video-tag' } | null {
  if (embed.embedType === 'video-tag') return { url: embed.url, embedType: 'video-tag' }
  if (embed.embedType !== 'iframe') return null
  let host = ''
  try {
    host = new URL(embed.url).hostname.toLowerCase()
  } catch {
    return null
  }
  if (
    host.includes('vimeo.com') ||
    host.includes('youtube.com') ||
    host.includes('youtube-nocookie.com')
  ) {
    return { url: toBackgroundEmbed(embed.url), embedType: 'iframe' }
  }
  if (host.includes('cloudflarestream.com') || host.includes('videodelivery.net')) {
    try {
      const u = new URL(embed.url)
      u.searchParams.set('autoplay', 'true')
      u.searchParams.set('muted', 'true')
      u.searchParams.set('loop', 'true')
      u.searchParams.set('controls', 'false')
      return { url: u.toString(), embedType: 'iframe' }
    } catch {
      return null
    }
  }
  return null
}
